import {
  convertObjectToParams,
  DATE_FORMAT_V2,
  DATE_FORMAT_V2_FULL,
  downloadFile,
  formatDateToString,
  getInfoStaff,
  getInfoStaffFull,
  PAGE_DEFAULT,
} from "@/utils";
import {
  HISTORY_PROJECT_LEAVE_LIST,
  PROJECT_LEAVE_BY_EMPLOYEE_INFO,
  PROJECT_LEAVE_BY_EMPLOYEE_LIST,
  PROJECT_LEAVE_LIST,
  SET_PAGES,
} from "../mutation-types";
import { formatHistory } from "./annualLeave/leaveCarryover";
import http from "./helpers/httpInterceptor";

const initialProjectLeaveByEmployeeInfo = {
  totalExpired: 0,
  totalLeaveDay: 0,
  totalRemain: 0,
  totalUsed: 0,
};
const state = () => ({
  projectLeaveList: [],
  projectLeaveByEmployeeList: [],
  projectLeaveByEmployeeInfo: initialProjectLeaveByEmployeeInfo,
  historyProjectLeaveList: [],
  pages: PAGE_DEFAULT,
});

// mutations
const mutations = {
  [PROJECT_LEAVE_LIST](states, data) {
    states.projectLeaveList = formatData(data);
  },
  [PROJECT_LEAVE_BY_EMPLOYEE_LIST](states, data) {
    states.projectLeaveByEmployeeList = formatData(data);
  },
  [PROJECT_LEAVE_BY_EMPLOYEE_INFO](state, data) {
    state.projectLeaveByEmployeeInfo = data;
  },
  [HISTORY_PROJECT_LEAVE_LIST](state, data) {
    state.historyProjectLeaveList = formatHistory(data);
  },
  [SET_PAGES](state, data) {
    state.pages = data;
  },
};

// actions
const actions = {
  getProjectLeaveList({ commit }, payload) {
    return http
      .get(`/tms/leaves-management/project-leave-v2${convertObjectToParams(payload)}`)
      .then((res) => {
        const {
          data,
          page: { page, size, total_element },
        } = res.data;
        commit(PROJECT_LEAVE_LIST, data);
        commit(SET_PAGES, {
          page,
          size,
          total: total_element,
        });
        return res.data;
      })
      .catch((error) => {
        commit(PROJECT_LEAVE_LIST, []);
        commit(SET_PAGES, PAGE_DEFAULT);
        throw error;
      });
  },
  getProjectLeaveByEmployeeList({ commit }, payload) {
    return http
      .get(`/tms/leaves-management/project-leave/employees${convertObjectToParams(payload)}`)
      .then((res) => {
        const {
          data,
          page: { page, size, total_element, advance_object },
        } = res.data;
        commit(PROJECT_LEAVE_BY_EMPLOYEE_LIST, data);
        commit(PROJECT_LEAVE_BY_EMPLOYEE_INFO, {
          ...advance_object,
          fullName: getInfoStaff(advance_object?.employee),
          fullNameStaff: getInfoStaffFull(advance_object?.employee),
          userName: advance_object.employee?.user_name ?? "",
        });
        commit(SET_PAGES, {
          page,
          size,
          total: total_element,
        });
        return res.data;
      })
      .catch((error) => {
        commit(PROJECT_LEAVE_BY_EMPLOYEE_LIST, []);
        commit(PROJECT_LEAVE_BY_EMPLOYEE_INFO, initialProjectLeaveByEmployeeInfo);
        commit(SET_PAGES, PAGE_DEFAULT);
        throw error;
      });
  },
  addProjectLeaveByProject(_, payload) {
    return http
      .post(`/tms/leaves-management/project-leave/batch-create`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  addProjectLeaveByEmployee(_, payload) {
    return http
      .post(`/tms/leaves-management/project-leave/create`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  addProjectLeaveV2(_, payload) {
    return http
      .post(`/tms/leaves-management/project-leave/create-v2`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  editProjectLeaveByEmployee(_, payload) {
    return http
      .put(`/tms/leaves-management/project-leave/edit`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  editProjectLeaveByEmployeeV2(_, payload) {
    return http
      .put(`/tms/leaves-management/project-leave/edit-v2`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  deleteProjectLeaveByEmployee(_, payload) {
    return http
      .put(`/tms/leaves-management/project-leave/update-status`, payload)
      .then((res) => {
        return res.data;
      })
      .catch((error) => {
        throw error;
      });
  },
  downloadAttachFileProjectLeave(_, payload) {
    return http
      .get(`/tms/leaves-management/project-leave/download-attach-file${convertObjectToParams(payload)}`, {
        responseType: "blob",
      })
      .then((res) => {
        const { filename } = res.headers;
        downloadFile(res.data, filename ?? "ProjectLeave.zip", res.headers["content-type"]);
        return res;
      })
      .catch((error) => {
        throw error;
      });
  },
  historyProjectLeave({ commit }, payload) {
    return http
      .get(`/tms/leaves-management/project-leave/history${convertObjectToParams(payload)}`)
      .then((res) => {
        commit(HISTORY_PROJECT_LEAVE_LIST, res.data.data);
        return res.data;
      })
      .catch((error) => {
        commit(HISTORY_PROJECT_LEAVE_LIST, []);
        throw error;
      });
  },
  importProjectLeave(_, payload) {
    const formData = new FormData();
    formData.append("file", payload[0]);
    return http
      .post(`/tms/leaves-management/project-leave/import`, formData, { responseType: "blob" })
      .then((res) => {
        const { filename, code } = res.headers;
        if (code != 200) {
          downloadFile(res.data, filename ?? "ProjectLeave.xlsx", res.headers["content-type"]);
        }
        return res;
      })
      .catch((error) => {
        throw error;
      });
  },
  downloadTemplate() {
    return http
      .get(`/tms/leaves-management/project-leave/template`, { responseType: "blob" })
      .then((res) => {
        const { filename } = res.headers;
        downloadFile(res.data, filename ?? "Template.xlsx", res.headers["content-type"]);
        return res;
      })
      .catch((error) => {
        throw error;
      });
  },
};

const formatData = (data) => {
  if (!data || data.length <= 0) return [];
  return data?.map((item) => {
    const { employee, project, applyDate, expireDate } = item;
    return {
      ...item,
      fullName: getInfoStaff(employee),
      fullNameStaff: getInfoStaffFull(employee),
      userName: employee?.user_name,
      division: employee?.division_name?.name ?? "",
      department: employee?.department_name?.name ?? "",
      employeeId: employee?.id ?? null,
      projectCode: project?.projectCode ?? "",
      projectName: project?.projectName ?? "",
      applyDateFormat: formatDateToString(applyDate, DATE_FORMAT_V2, DATE_FORMAT_V2_FULL),
      expireDateFormat: formatDateToString(expireDate, DATE_FORMAT_V2, DATE_FORMAT_V2_FULL),
    };
  });
};
export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
